import { unifiedInboxSupportConversationKey } from "@circle-react/lib/queries/unifiedInboxSupportConversations";
import { useMutation, useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";

import { CIRCLE_AI_CONVERSATIONS_QUERY_KEY } from "./Home/RecentChatsDrawer/useRecentChatsList";
import { useOpenCircleAi } from "./useOpenCircleAi";

interface OfferHumanSupportResponse {
  conversation_id: string;
}

const isOfferHumanSupportResponse = (
  value: unknown,
): value is OfferHumanSupportResponse =>
  typeof value === "object" &&
  value !== null &&
  "conversation_id" in value &&
  typeof value.conversation_id === "string";

export const useOfferHumanSupportCta = () => {
  const queryClient = useQueryClient();
  const toastr = useToast();
  const { openConversation } = useOpenCircleAi();

  const mutation = useMutation({
    mutationFn: () =>
      reactQueryPost<unknown>(internalApi.circleAiOfferHumanSupport.create()),
    onError: (error: unknown) => {
      toastr.error(
        error instanceof Error ? error.message : t("request_failure_message"),
      );
    },
    onSuccess: data => {
      if (!isOfferHumanSupportResponse(data)) {
        toastr.error(t("request_failure_message"));
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: [CIRCLE_AI_CONVERSATIONS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: unifiedInboxSupportConversationKey.listRoot(),
        exact: false,
      });
      openConversation(data.conversation_id);
    },
  });

  return {
    handleOfferHumanSupport: mutation.mutate,
    isOfferHumanSupportLoading: mutation.isPending,
  };
};
